import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Phone, Bell, Eye, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { riskLevel, RISK_STYLES, timeAgo } from "@/lib/appUtils";
import { useBranding } from "@/lib/branding";
import { cn } from "@/lib/utils";

function riskColorClass(rl) {
  if (rl === "high") return "text-rose-600 dark:text-rose-400";
  if (rl === "moderate") return "text-amber-600 dark:text-amber-400";
  return "text-emerald-600 dark:text-emerald-400";
}

/** Hero-style risk score triage card. Number is the star; badge subtle. */
export function TriageView({ patients }) {
  const { hospCode } = useBranding();
  const sorted = [...patients].sort((a, b) => b.risk_score - a.risk_score);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="triage-view">
      {sorted.map((p) => {
        const rl = riskLevel(p.risk_score);
        const style = RISK_STYLES[rl];
        const flag = p.risk_score > 70;
        const numberColor = riskColorClass(rl);
        return (
          <div
            key={p.id}
            data-testid={`triage-row-${p.id}`}
            className="surface-card p-6 shadow-sm hover:shadow-md transition-all flex flex-col gap-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {flag && (
                    <span
                      data-testid={`red-flag-${p.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-rose-600 dark:text-rose-400"
                      title="High risk red flag"
                    >
                      <AlertTriangle className="w-3 h-3" /> Red flag
                    </span>
                  )}
                </div>
                <div className="font-semibold text-foreground truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {p.surgery_type} · Day {p.recovery_day}/{p.care_plan_duration || 20}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Last check-in {timeAgo(p.last_checkin)}</div>
              </div>

              {/* Hero risk number */}
              <div className="text-right shrink-0">
                <div className={cn("text-5xl sm:text-6xl font-bold tracking-tight leading-none tabular-nums", numberColor)}>
                  {p.risk_score}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mt-1">
                  risk score
                </div>
                <span className={cn(
                  "inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border mt-2",
                  style.badge
                )}>
                  {style.label}
                </span>
              </div>
            </div>

            <div>
              <div className="h-1 rounded-full bg-secondary overflow-hidden">
                <div className={cn("h-full", style.bar)} style={{ width: `${p.risk_score}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground mt-1.5 uppercase tracking-wider font-medium">
                <span>0</span><span>Threshold 70</span><span>100</span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
              <Button
                variant="outline" size="sm" className="rounded-full text-xs h-8"
                data-testid={`call-btn-${p.id}`}
                onClick={() => toast.success(`Calling ${p.name}…`, { description: p.contact_phone })}
              >
                <Phone className="w-3 h-3 mr-1.5" /> Call
              </Button>
              <Button
                variant="outline" size="sm" className="rounded-full text-xs h-8"
                data-testid={`remind-btn-${p.id}`}
                onClick={() => toast.success(`Reminder queued for ${p.name}`)}
              >
                <Bell className="w-3 h-3 mr-1.5" /> Remind
              </Button>
              <Button
                asChild size="sm"
                className="rounded-full text-xs h-8 ml-auto"
                data-testid={`view-btn-${p.id}`}
              >
                <Link to={`/${hospCode}/doctor/patient/${p.id}`}>
                  <Eye className="w-3 h-3 mr-1.5" /> View details <ArrowUpRight className="w-3 h-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        );
      })}
      {sorted.length === 0 && (
        <div className="lg:col-span-2 surface-card p-10 text-center text-muted-foreground">No patients to triage yet.</div>
      )}
    </div>
  );
}
